<script setup lang="ts">
import type { Badge } from "@innoclouds/api";
import { TintedIcon } from "@innoclouds/ui";
import { useTranslation } from "i18next-vue";
import { computed } from "vue";
import { useBadgesStore } from "#/store/badges";
import { type BadgeShape, badgeBgColor, badgeTextColor, resolveBadgeShape } from "#/utils/badges";
import { getLocaleFor } from "#/utils/i18next";

interface Props {
	badge: Badge;
	defaultShape?: BadgeShape;
	size?: "md" | "sm";
}

const BADGE_LABEL_MAX_LENGTH = 15;

const props = withDefaults(defineProps<Props>(), { defaultShape: "pill", size: "md" });

const { i18next } = useTranslation();
const badgesStore = useBadgesStore();

const shape = computed(() => resolveBadgeShape(props.badge, props.defaultShape));
const background = computed(() => badgeBgColor(props.badge));
const contentColor = computed(() => props.badge.icon_color || badgeTextColor(background.value));
const iconSize = computed(() => (props.size === "sm" ? 16 : 24));
const rawLabel = computed(() => getLocaleFor("label", props.badge, i18next.language) || props.badge.label);
const label = computed(() => (rawLabel.value?.length > BADGE_LABEL_MAX_LENGTH ? `${rawLabel.value.slice(0, BADGE_LABEL_MAX_LENGTH)}…` : rawLabel.value));
const iconSrc = computed(() => badgesStore.iconUrl(props.badge));
const isVector = computed(() => badgesStore.isVectorBadgeIcon(props.badge));
const withText = computed(() => shape.value !== "icon_only" && !!label.value);
</script>

<template>
	<span class="badge_item" :class="[`badge_${shape}`, `badge_size_${size}`]">
		<span class="badge_body" :style="{ backgroundColor: background, color: contentColor }">
			<TintedIcon v-if="iconSrc" :src="iconSrc" :vector="isVector" :color="contentColor" :size="iconSize" class="badge_icon" />
			<span v-if="withText" class="badge_label">{{ label }}</span>
		</span>
	</span>
</template>

<style scoped lang="scss">
  @use './badgeItem.scss';
</style>
